import { useContext, useLayoutEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSetRecoilState } from 'recoil';

import {
  ChainlitContext,
  IChainlitConfig,
  configState,
  useAuth,
  useChatSession,
  useConfig
} from '@chainlit/react-client';

// Refresh native settings only while the empty chat is visible. No socket work.
export function useStarterCatalogRefresh(empty: boolean) {
  const client = useContext(ChainlitContext);
  const { config } = useConfig();
  const { user } = useAuth();
  const { chatProfile } = useChatSession();
  const { i18n } = useTranslation();
  const setConfig = useSetRecoilState(configState);
  const interval = config?.starterCatalogRefreshSeconds;
  const language = i18n.language || navigator.language || 'en-US';

  useLayoutEffect(() => {
    if (!interval) return;
    // Clear before paint when identity/language/profile changes.
    setConfig((current) => current && { ...current, starterCategories: [] });
    if (!empty || !user) return;
    let cancelled = false;
    let timer: ReturnType<typeof setTimeout>;
    const refresh = async () => {
      try {
        const params = new URLSearchParams({ language });
        if (chatProfile) params.set('chat_profile', chatProfile);
        const response = await client.get(`/project/settings?${params}`);
        const settings: IChainlitConfig = await response.json();
        if (!cancelled) {
          setConfig(
            (current) =>
              current && {
                ...current,
                starterCategories: settings.starterCategories,
                starterCatalogRefreshSeconds:
                  settings.starterCatalogRefreshSeconds
              }
          );
        }
      } catch {
        if (!cancelled) {
          setConfig(
            (current) => current && { ...current, starterCategories: [] }
          );
        }
      } finally {
        if (!cancelled) timer = setTimeout(refresh, interval * 1000);
      }
    };
    void refresh();
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    empty,
    user?.identifier,
    interval,
    language,
    chatProfile,
    client,
    setConfig
  ]);
}
